import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Permission } from '../enums/permission.enum';
import {
  DOMAIN_ROLE_HIERARCHY,
  RoleLayer,
  UserRole,
} from '../enums/role.enum';
import {
  selectUserAccessProfile,
  selectActiveDomainAccess,
  selectActivePermissions,
  selectIsAccountLevel,
  selectGlobalPermissions,
} from '../../../store/entities/user/user.selectors';
import { DomainAccess, UserAccessProfile } from '../interfaces/rbac.interface';

@Injectable({
  providedIn: 'root',
})
export class RbacService {
  private readonly store = inject(Store);

  private readonly userAccessProfile = toSignal(
    this.store.select(selectUserAccessProfile),
    { initialValue: null }
  );

  private readonly activeDomainAccess = toSignal(
    this.store.select(selectActiveDomainAccess),
    { initialValue: null }
  );

  private readonly activePermissions = toSignal(
    this.store.select(selectActivePermissions),
    { initialValue: [] as Permission[] }
  );

  private readonly isAccountLevel = toSignal(
    this.store.select(selectIsAccountLevel),
    { initialValue: false }
  );

  private readonly globalPermissions = toSignal(
    this.store.select(selectGlobalPermissions),
    { initialValue: [] as Permission[] }
  );

  getUserAccessProfile(): UserAccessProfile | null {
    return this.userAccessProfile();
  }

  getCurrentDomainAccess(): DomainAccess | null {
    return this.activeDomainAccess();
  }

  can(permission: Permission): boolean {
    if (this.hasAccountAdminAccess()) {
      return true;
    }

    return this.activePermissions().includes(permission);
  }

  canAll(permissions: readonly Permission[]): boolean {
    return permissions.every((permission) => this.can(permission));
  }

  canAny(permissions: readonly Permission[]): boolean {
    return permissions.some((permission) => this.can(permission));
  }

  canGlobally(permission: Permission): boolean {
    return this.hasAccountAdminAccess() || this.globalPermissions().includes(permission);
  }

  hasAccountRole(role: UserRole): boolean {
    const profile = this.userAccessProfile();

    if (!profile) {
      return false;
    }

    return (
      profile.accountRole === role ||
      profile.allRoles.some(
        (userRole) => userRole.code === role && userRole.layer === RoleLayer.ACCOUNT
      )
    );
  }

  hasCurrentDomainRole(role: UserRole): boolean {
    return this.activeDomainAccess()?.roles.some(
      (userRole) => userRole.layer === RoleLayer.DOMAIN && userRole.code === role
    ) ?? false;
  }

  hasRoleInDomain(role: UserRole, domainUuid: string): boolean {
    const domainAccess = this.userAccessProfile()?.domainAccess.get(domainUuid);

    return domainAccess?.roles.some(
      (userRole) => userRole.layer === RoleLayer.DOMAIN && userRole.code === role
    ) ?? false;
  }

  hasMinimumDomainRole(minimumRole: UserRole, domainUuid?: string): boolean {
    if (this.hasAccountAdminAccess()) {
      return true;
    }

    const minimumLevel = DOMAIN_ROLE_HIERARCHY[minimumRole];

    if (minimumLevel === undefined) {
      return false;
    }

    const domainAccess = domainUuid
      ? this.userAccessProfile()?.domainAccess.get(domainUuid) ?? null
      : this.activeDomainAccess();

    return this.getHighestDomainRoleLevel(domainAccess) >= minimumLevel;
  }

  isAccountAdmin(): boolean {
    return this.hasAccountRole(UserRole.ACCOUNT_ADMIN);
  }

  isCurrentDomainAdmin(): boolean {
    return this.hasAccountAdminAccess() || this.hasCurrentDomainRole(UserRole.DOMAIN_ADMIN);
  }

  getGlobalPermissions(): readonly Permission[] {
    return this.globalPermissions();
  }

  getActivePermissions(): readonly Permission[] {
    return this.activePermissions();
  }

  private hasAccountAdminAccess(): boolean {
    return this.isAccountAdmin();
  }

  private getHighestDomainRoleLevel(domainAccess: DomainAccess | null): number {
    if (!domainAccess) {
      return -1;
    }

    return domainAccess.roles.reduce((highestLevel, roleAssignment) => {
      const roleLevel = DOMAIN_ROLE_HIERARCHY[roleAssignment.code] ?? -1;

      return Math.max(highestLevel, roleLevel);
    }, -1);
  }
}
